import tokenRefreshManager from '../../utils/auth/tokenRefreshManager';

// Uploads API methods — mixin applied to the ApiService prototype chain.
// Methods run with `this` bound to the ApiService instance, so this.request()
// and cross-domain calls resolve through the shared HttpClient base.
const withUploadsApi = (Base) => class extends Base {
  // File upload endpoints
  async requestWithUploadProgress(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const method = options.method || 'POST';
    const onUploadProgress = options.onUploadProgress;
    const body = options.body;

    const token = localStorage.getItem('faithinsight_token');
    let csrfToken = null;
    if (token && ['POST', 'PUT', 'DELETE', 'PATCH'].includes(method.toUpperCase())) {
      csrfToken = await this.fetchCSRFToken();
      if (!csrfToken) {
        const error = new Error('Failed to obtain CSRF token. This may be due to a network issue. Please check your connection and try again, or refresh the page.');
        error.csrfTokenMissing = true;
        error.retryable = true;
        error.isNetworkError = true;
        throw error;
      }
    }

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open(method, url, true);
      xhr.responseType = 'json';

      xhr.setRequestHeader('Accept', 'application/json');
      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.setRequestHeader('X-Token-Version', tokenRefreshManager.getTokenVersion().toString());
      }
      if (csrfToken) {
        xhr.setRequestHeader('X-CSRF-Token', csrfToken);
      }

      xhr.upload.onprogress = (event) => {
        if (!onUploadProgress || !event.lengthComputable) return;
        onUploadProgress({
          loaded: event.loaded,
          total: event.total,
          percent: Math.min(100, Math.round((event.loaded / event.total) * 100))
        });
      };

      xhr.onload = () => {
        const data = xhr.response || {};
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(data);
          return;
        }

        const error = new Error(data.message || data.error || `HTTP error! status: ${xhr.status}`);
        error.status = xhr.status;
        error.data = data;
        reject(error);
      };

      xhr.onerror = () => {
        const error = new Error('Network error. Please check your connection and try again.');
        error.isNetworkError = true;
        reject(error);
      };

      xhr.onabort = () => {
        const error = new Error('Upload cancelled.');
        error.name = 'AbortError';
        reject(error);
      };

      xhr.send(body);
    });
  }

  async uploadFile(file, type = 'assignment') {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);

    return this.request('/files/upload', {
      method: 'POST',
      headers: {
        // Remove Content-Type to let browser set it with boundary
        'Content-Type': undefined
      },
      body: formData
    });
  }

  async downloadFile(fileId) {
    return this.request(`/files/${fileId}/download`, {
      method: 'GET'
    });
  }

};

export default withUploadsApi;
